// Cloud sync layer — makes each authenticated user's application data
// (Chronicles/events, tasks, settings, notes, goals) permanent on the backend
// and restores it automatically on login. localStorage remains the fast local
// cache and keeps working offline; changes are debounce-pushed to the cloud and
// auto-flushed when the connection returns. Model: last-write-wins snapshot.

import { api } from "./api";
import { logWarn, ignore } from "./log";
import { scopedKey, getActiveUid } from "./userScope";
import { STORAGE_KEY as LANG_KEY, LANGS } from "@/i18n/locale";

const THEME_KEY = "chronicles.theme";
const RES_KEY = "chronicles.calendar.resolution";

let syncEnabled = false;
let hydrating = false;
let dirty = false;
let pushTimer = null;

function readJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function collectSnapshot() {
  return {
    events: readJSON(scopedKey("events.v1"), []),
    tasks: readJSON(scopedKey("tasks.v1"), []),
    settings: {
      theme: localStorage.getItem(THEME_KEY) || null,
      language: localStorage.getItem(LANG_KEY) || null,
      calendarResolution: localStorage.getItem(RES_KEY) || null,
    },
    notes: [],
    goals: [],
    planningMemory: readJSON(scopedKey("planningMemory.v1"), {}),
    bulkJobs: readJSON(scopedKey("bulkJobs.v1"), []),
  };
}

function hydrateFromCloud(data) {
  hydrating = true;
  try {
    const events = Array.isArray(data.events) ? data.events : [];
    const tasks = Array.isArray(data.tasks) ? data.tasks : [];
    localStorage.setItem(scopedKey("events.v1"), JSON.stringify(events));
    localStorage.setItem(scopedKey("tasks.v1"), JSON.stringify(tasks));
    if (data.planningMemory && typeof data.planningMemory === "object") {
      localStorage.setItem(scopedKey("planningMemory.v1"), JSON.stringify(data.planningMemory));
    }
    if (Array.isArray(data.bulkJobs)) {
      localStorage.setItem(scopedKey("bulkJobs.v1"), JSON.stringify(data.bulkJobs));
    }

    const s = data.settings || {};
    if (s.theme) localStorage.setItem(THEME_KEY, s.theme);
    if (s.language && LANGS.includes(s.language)) localStorage.setItem(LANG_KEY, s.language);
    if (s.calendarResolution) localStorage.setItem(RES_KEY, String(s.calendarResolution));

    // Notify the UI to re-read local caches and re-apply theme/language.
    window.dispatchEvent(new CustomEvent("chronicles:changed"));
    window.dispatchEvent(new CustomEvent("chronicles:tasks-changed"));
    window.dispatchEvent(new CustomEvent("chronicles:settings-changed"));
    window.dispatchEvent(new CustomEvent("chronicles:memory-changed"));
  } finally {
    hydrating = false;
  }
}

async function pushNow() {
  if (!syncEnabled || !getActiveUid()) return;
  try {
    await api.put("/me/data", collectSnapshot());
    dirty = false;
  } catch (e) {
    dirty = true; // keep dirty; retry on next change or when back online
    logWarn("cloud sync push failed, will retry.", e);
  }
}

function schedulePush() {
  if (!syncEnabled || hydrating) return;
  dirty = true;
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(pushNow, 1200);
}

function onOnline() {
  if (dirty) pushNow();
}

function mergeById(cloudArr, localArr) {
  const map = new Map();
  for (const it of Array.isArray(cloudArr) ? cloudArr : []) {
    if (it && it.id != null) map.set(it.id, it);
  }
  // Local (this device) overrides cloud on id conflicts. Dedupe by id prevents
  // duplicate imports.
  for (const it of Array.isArray(localArr) ? localArr : []) {
    if (it && it.id != null) map.set(it.id, it);
  }
  return Array.from(map.values());
}

function migratedKeyName() {
  return scopedKey("cloudMigrated.v1");
}

function markMigrated() {
  try {
    localStorage.setItem(migratedKeyName(), new Date().toISOString());
  } catch {
    ignore();
  }
}

function isMigrated() {
  try {
    return !!localStorage.getItem(migratedKeyName());
  } catch {
    return false;
  }
}

function writeLocal(events, tasks) {
  localStorage.setItem(scopedKey("events.v1"), JSON.stringify(events || []));
  localStorage.setItem(scopedKey("tasks.v1"), JSON.stringify(tasks || []));
  window.dispatchEvent(new CustomEvent("chronicles:changed"));
  window.dispatchEvent(new CustomEvent("chronicles:tasks-changed"));
}

// Reconciliation state captured when un-migrated local data is found at login.
let pending = null;

function attachListeners() {
  if (syncEnabled) return;
  syncEnabled = true;
  window.addEventListener("chronicles:changed", schedulePush);
  window.addEventListener("chronicles:tasks-changed", schedulePush);
  window.addEventListener("chronicles:settings-changed", schedulePush);
  window.addEventListener("chronicles:memory-changed", schedulePush);
  window.addEventListener("chronicles:bulkjobs-changed", schedulePush);
  window.addEventListener("online", onOnline);
}

// Pull the user's cloud data and hydrate the local cache, then begin
// auto-syncing. If un-migrated local Chronicle data is found, defer and ask the
// user (via the chronicles:migration-needed event) instead of silently
// importing or overwriting anything.
export async function startSync() {
  let exists = false;
  let cloud = { events: [], tasks: [], settings: {} };
  try {
    const { data } = await api.get("/me/data");
    exists = !!(data && data.exists);
    cloud = (data && data.data) || cloud;
  } catch (e) {
    // Offline / transient: keep working from local cache and sync later.
    logWarn("cloud data fetch failed, using local cache.", e);
    attachListeners();
    dirty = true;
    return;
  }

  const local = collectSnapshot();
  const localHasData = local.events.length > 0 || local.tasks.length > 0;

  // Returning cloud user (already reconciled on this device): cloud is truth.
  if (isMigrated()) {
    hydrateFromCloud(cloud);
    attachListeners();
    return;
  }

  // First reconciliation on this device and no local data → adopt cloud.
  if (!localHasData) {
    hydrateFromCloud(cloud);
    markMigrated();
    attachListeners();
    return;
  }

  // Un-migrated local data exists → ask the user what to do. Do NOT hydrate,
  // push or attach listeners yet.
  pending = { cloud, exists };
  window.dispatchEvent(
    new CustomEvent("chronicles:migration-needed", {
      detail: { localEvents: local.events.length, localTasks: local.tasks.length },
    })
  );
}

// Resolve the local-data migration prompt. choice: "import" | "keepLocal" | "cancel".
export async function resolveMigration(choice) {
  const p = pending;
  pending = null;
  const cloud = (p && p.cloud) || { events: [], tasks: [], settings: {} };
  const local = collectSnapshot();

  if (choice === "import") {
    // Merge local into the account (dedupe by id), keep both, push up.
    const events = mergeById(cloud.events, local.events);
    const tasks = mergeById(cloud.tasks, local.tasks);
    writeLocal(events, tasks);
    markMigrated();
    attachListeners();
    await pushNow();
    return;
  }

  if (choice === "keepLocal") {
    // Keep local data on this device (backed up, never deleted); the account
    // uses its own cloud data going forward. Cloud is never overwritten here.
    try {
      localStorage.setItem(scopedKey("events.premigration-backup.v1"), JSON.stringify(local.events));
      localStorage.setItem(scopedKey("tasks.premigration-backup.v1"), JSON.stringify(local.tasks));
    } catch {
      ignore();
    }
    hydrateFromCloud(cloud);
    markMigrated();
    attachListeners();
    return;
  }

  // Cancel: decide later. Preserve everything (union cloud into local working
  // set so nothing is lost) and re-prompt on next login. Do not mark migrated.
  const events = mergeById(cloud.events, local.events);
  const tasks = mergeById(cloud.tasks, local.tasks);
  writeLocal(events, tasks);
  attachListeners();
}


export async function flushPendingSync() {
  if (pushTimer) {
    clearTimeout(pushTimer);
    pushTimer = null;
  }
  if (dirty) await pushNow();
}

export function stopSync() {
  syncEnabled = false;
  dirty = false;
  pending = null;
  if (pushTimer) {
    clearTimeout(pushTimer);
    pushTimer = null;
  }
  window.removeEventListener("chronicles:changed", schedulePush);
  window.removeEventListener("chronicles:tasks-changed", schedulePush);
  window.removeEventListener("chronicles:settings-changed", schedulePush);
  window.removeEventListener("chronicles:memory-changed", schedulePush);
  window.removeEventListener("chronicles:bulkjobs-changed", schedulePush);
  window.removeEventListener("online", onOnline);
}
